import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, ArrowLeft, Tag, Share2 } from "lucide-react";
import DOMPurify from "dompurify";
import axios from "axios";

import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
      setLoading(false);
    } catch (e) {
      console.error("Error fetching post:", e);
      setError("Post not found");
      setLoading(false);
    }
  };

  const getCategoryStyle = (catId) => {
    const styles = {
      sponsor: "bg-[#D4A017] text-[#1A2F23]",
      premium: "bg-[#2C4A3B] text-white",
      news: "bg-[#3B82F6] text-white",
      community: "bg-[#B85C38] text-white"
    };
    return styles[catId] || "bg-[#5C5A52] text-white";
  };

  const getCategoryName = (catId) => {
    const names = {
      sponsor: "Sponsor Promotion",
      premium: "Premium Service",
      news: "Valley News",
      community: "Community"
    };
    return names[catId] || catId;
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EFEDE5]">
        <NavbarLight />
        <div className="flex items-center justify-center py-40">
          <div className="text-[#5C5A52]">Loading...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-[#EFEDE5]">
        <NavbarLight />
        <div className="flex flex-col items-center justify-center py-40 px-6">
          <Tag className="w-16 h-16 text-[#D1CFC5] mb-4" />
          <h1 className="font-heading text-3xl font-bold text-[#1A1915] mb-2">Post Not Found</h1>
          <p className="text-[#5C5A52] mb-6">The post you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <Link to="/blog">
            <Button className="bg-[#2C4A3B] text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <NavbarLight />

      {/* Back Link */}
      <div className="px-6 md:px-12 pt-6">
        <div className="max-w-3xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#5C5A52] hover:text-[#2C4A3B]">
            <ArrowLeft className="w-4 h-4" />
            Back to all posts
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-8 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className={`${getCategoryStyle(post.category)} text-sm px-4 py-1 rounded-full font-medium`}>
                {getCategoryName(post.category)}
              </span>
            </div>
            
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915] mb-4">
              {post.title}
            </h1>
            
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 text-[#5C5A52]">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.created_at).toLocaleDateString('en-US', { 
                    weekday: 'long',
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                </span>
                <span>By {post.author}</span>
              </div>
              <Button variant="outline" size="sm" onClick={handleShare}>
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="mb-8 rounded-xl overflow-hidden border-2 border-[#D1CFC5]">
              <img 
                src={post.image_url} 
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none text-[#1A1915]"
            style={{
              lineHeight: '1.8',
            }}
          >
            {/* Render rich HTML content or fallback to plain text paragraphs */}
            {post.content.includes('<') ? (
              <div
                className="text-[#5C5A52] [&_h1]:text-[#1A1915] [&_h1]:font-heading [&_h2]:text-[#1A1915] [&_h2]:font-heading [&_h3]:text-[#1A1915] [&_h3]:font-heading [&_a]:text-[#2C4A3B] [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-[#D4A017] [&_blockquote]:pl-4 [&_blockquote]:italic [&_img]:rounded-lg [&_img]:my-4"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
                data-testid="blog-rich-content"
              />
            ) : (
              post.content.split('\n').map((paragraph, idx) => (
                paragraph.trim() && (
                  <p key={idx} className="mb-4 text-[#5C5A52]">
                    {paragraph}
                  </p>
                )
              ))
            )}
          </div>

          {/* Footer CTA */}
          <div className="mt-12 pt-8 border-t-2 border-[#D1CFC5]">
            <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 text-center">
              <p className="text-[#5C5A52] mb-4">Enjoyed this post? Support local content.</p>
              <Link to="/donate">
                <Button className="bg-[#D4A017] hover:bg-[#E5B945] text-[#1A2F23] font-semibold">
                  Support ekvalleys.com
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
